import { FC, useState } from 'react';
import type {
  IngredientsDetailTopIngredsInner,
  IngredientsDetailTopIngredsInnerDetailsInner,
} from '@commerce-product-webcomponents/sdk';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  Theme,
  Modal,
  TextAlign,
  Drawer,
  Breakpoints,
  NextgenDOMPurify,
  useBreakpoint,
  useTranslation,
} from '@nextgen-web-framework/all';
import { ImageComponent, ImageType as ImageClassNameType } from '../../Molecules';
import { keyCheck } from '../../../utils';
import { ProductIngredientsPropsTypes } from './types/propsTypes';
import styles from './ProductIngredients.module.scss';

const ProductIngredients: FC<ProductIngredientsPropsTypes> = ({
  ingredientsData: { ingredientsDetail, ingredients },
}): JSX.Element => {
  const t = useTranslation();
  const [isIngredientsExpanded, setIsIngredientsExpanded] = useState<boolean>(false);
  const handleIngredientsExpansion = () => setIsIngredientsExpanded(!isIngredientsExpanded);
  const [showSupTable, setShowSupTable] = useState<boolean>(false);
  const ingredientsExpanderLabel = `${t('product.productIngredient.ingredientView')} ${
    isIngredientsExpanded ? t('product.productIngredient.viewLess') : t('product.productIngredient.viewMore')
  }`;
  const ingredientsText = ingredientsDetail?.ingredSectionText || '';
  const learnMorelinkHTML = ingredientsDetail?.ingredLinkUrl
    ? `<a href=${ingredientsDetail?.ingredLinkUrl || ''} class="${
        styles['product-learn-more-link']
      }" target="_blank">${t('product.common.learnMore')}</a>`
    : '';
  const combinedIngredientsTextHTML = `${ingredientsText}${learnMorelinkHTML}`;
  const ingredientsExpander = (
    <b
      role="button"
      onClick={handleIngredientsExpansion}
      onKeyDown={(e) => {
        if (keyCheck(e.code)) {
          handleIngredientsExpansion();
        }
      }}
      tabIndex={0}
      className={`${styles['product-ingredient-expander']} ${isIngredientsExpanded ? styles['expanded'] : ''}`}
      aria-expanded={isIngredientsExpanded}
      aria-controls="product-ingredients-list"
      aria-label={ingredientsExpanderLabel}
      data-testid="product-ingredients-expander"
    >
      &nbsp;
      {ingredientsExpanderLabel}
    </b>
  );

  const openSupplementsTableHandler = () => {
    setShowSupTable(true);
  };
  const closeSupplementsTableHandler = () => {
    setShowSupTable(false);
  };

  const viewportType = useBreakpoint();
  let supplementTableComp = <></>;
  if (viewportType === Breakpoints.SM || viewportType === Breakpoints.XS) {
    supplementTableComp = (
      <Drawer
        header={t('product.productIngredient.headline')}
        closeOnBackdropClick={true}
        onClose={closeSupplementsTableHandler}
        closeIcon={true}
        bottomGap={0}
        footer={<Button title={t('product.common.done')} onClick={closeSupplementsTableHandler} />}
      >
        <span dangerouslySetInnerHTML={{ __html: NextgenDOMPurify(ingredientsDetail?.supplementFact || '') }} />
      </Drawer>
    );
  } else {
    supplementTableComp = (
      <Modal
        headline={t('product.productIngredient.headline')}
        primaryButtonLabel={t('product.common.done')}
        onPrimaryButtonClick={closeSupplementsTableHandler}
        onClose={closeSupplementsTableHandler}
        textAlign={TextAlign.CENTER}
      >
        <span dangerouslySetInnerHTML={{ __html: NextgenDOMPurify(ingredientsDetail?.supplementFact || '') }} />
      </Modal>
    );
  }
  return (
    <>
      {!!ingredients && (
        <div className={styles['product-ingredients-container']} data-testid="product-ingredients-container">
          <h3 className={styles['product-ingredients-main-title']}>
            {t('product.productIngredient.ingredientMainTitle')}
          </h3>
          <h2 className={styles['product-ingredients-sub-title']}>{ingredientsDetail?.ingredSectionTitle || ''}</h2>
          <div
            className={styles['product-ingredients-text']}
            dangerouslySetInnerHTML={{ __html: NextgenDOMPurify(combinedIngredientsTextHTML || '') }}
          />
          <div className={styles['product-ingredients-card-container']}>
            {ingredientsDetail?.topIngreds &&
              ingredientsDetail?.topIngreds.map((v: IngredientsDetailTopIngredsInner, i: number) => (
                <div
                  className={styles['product-ingredients-card']}
                  style={{ backgroundColor: v.bkgrdCssCode }}
                  key={v.title}
                  data-testid={`product-ingredients-card${i}`}
                >
                  <div className={styles['product-ingredients-img']}>
                    <ImageComponent
                      link={v.image?.main?.publicUrl || ''}
                      alt={v.image?.main?.altText || ''}
                      imagetype={ImageClassNameType.PRODUCT_INGREDIENT}
                    />
                  </div>
                  <div className={styles['product-ingredient-details']}>
                    <h4 className={styles['product-ingredient-title']}>{v.title}</h4>
                    <p className={styles['product-ingredient-summary']}>{v.text}</p>
                    <div className={styles['product-ingredient-grid']}>
                      {v.details &&
                        v.details.map(
                          ({ label, value }: IngredientsDetailTopIngredsInnerDetailsInner, index: number) => (
                            <div
                              className={styles['product-ingredient-grid-row']}
                              key={label}
                              data-testid={`product-ingredient-grid-row${i}${index}`}
                            >
                              <div className={`${styles['product-ingredient-cell']} ${styles['left-cell']}`}>
                                {label}
                              </div>
                              <div className={`${styles['product-ingredient-cell']} ${styles['right-cell']}`}>
                                {value}
                              </div>
                            </div>
                          ),
                        )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className={styles['product-ingredients-all-ingredients']}>
            <span
              className={`${isIngredientsExpanded ? '' : styles['product-ingredient-overflow']}`}
              id="product-ingredients-list"
            >
              <b>{t('product.productIngredient.ingredientSubTitle')}&nbsp;</b>
              {ingredients}
              {isIngredientsExpanded && ingredientsExpander}
            </span>
            {!isIngredientsExpanded && ingredientsExpander}
          </div>
          <div className={styles['product-ingredients-view-suplements-facts-container']}>
            {showSupTable && supplementTableComp}
            {!!ingredientsDetail?.supplementFact?.trim() && (
              <Button
                id="view-supplements-facts"
                title={t('product.productIngredient.ingredientButtonTitle')}
                tabIndex={0}
                size={ButtonSize.MEDIUM}
                buttonStyle={ButtonStyles.OUTLINE}
                theme={Theme.BLACK}
                data-testid="view-supplements-facts"
                onClick={openSupplementsTableHandler}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductIngredients;
export type { ProductIngredientsPropsTypes };
